import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import {
  FormControl,
  InputLabel,
  makeStyles,
  MenuItem,
  Select
} from "@material-ui/core";
import styles from "assets/jss/material-dashboard-pro-react/views/formsStyle.js";
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import CardIcon from "components/Card/CardIcon";
import { Assignment } from "@material-ui/icons";
import CardBody from "components/Card/CardBody";
import Button from "components/CustomButtons/Button.js";
import CustomInput from "components/CustomInput/CustomInput";
import ReactTable from "react-table";
import { orderApi } from "functions/apis";
import VisibilityIcon from "@material-ui/icons/Visibility";
import Datetime from "react-datetime";
import { formatCurrency, formatDateTime } from "functions/Helper";
import { GetOnlineOrderStatuses } from "enums/OnlineOrderStatus";
import { GetOnlineOrderStatusDescription } from "enums/OnlineOrderStatus";

const useStyles = makeStyles(styles);

export default function OnlineOrderList() {
  const onlineOrderSignal = new AbortController();

  const [data, setData] = useState();
  const [payload, setPayload] = useState({
    searchString: null,
    status: null,
    minTransactionTime: null,
    maxTransactionTime: null,
    sort: 7
  });
  const [pagination, setPagination] = useState({
    pageIndex: 1,
    pageSize: 10
  });
  const [loading, setLoading] = useState(false);
  const [pages, setPages] = useState(0);

  const getData = useCallback(() => {
    setLoading(true);

    orderApi
      .get("onlineOrders", {
        params: {
          filter: payload,
          pagination
        },
        signal: onlineOrderSignal.signal
      })
      .then(resp => {
        setData(
          resp.records.map(record => {
            return {
              ...record,
              actions: (
                <div className="actions-right">
                  <>
                    <Link to={"/admin/OnlineOrder/View/" + record.id}>
                      <Button
                        color="info"
                        className="like"
                        type="button"
                        value="VIEW"
                      >
                        <VisibilityIcon /> VIEW
                      </Button>
                    </Link>
                  </>
                </div>
              )
            };
          })
        );
        setPages(Math.ceil(resp.totalRecords / pagination.pageSize));
      })
      .catch(err => console.log(err))
      .finally(() => setLoading(false));
  }, [payload, pagination]);

  useEffect(() => {
    getData();
  }, [payload, pagination]);

  useEffect(() => {
    return () => onlineOrderSignal.abort();
  }, []);

  const handleSearchStringChanged = useCallback(
    e => {
      setPayload({
        ...payload,
        searchString: e.target.value === "" ? null : e.target.value
      });
    },
    [payload]
  );

  const handleStatusOptionChanged = useCallback(
    e => {
      setPayload({ ...payload, status: e.target.value });
    },
    [payload]
  );

  const handleMinTransactionTimeChanged = useCallback(
    e => {
      setPayload({ ...payload, minTransactionTime: e === "" ? null : e });
    },
    [payload]
  );

  const handleMaxTransactionTimeChanged = useCallback(
    e => {
      setPayload({ ...payload, maxTransactionTime: e === "" ? null : e });
    },
    [payload]
  );

  const classes = useStyles();
  return (
    <GridContainer>
      <GridItem xs={12}>
        <Card>
          <CardHeader color="primary" icon>
            <CardIcon color="primary">
              <Assignment />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>ONLINE ORDER</h4>
          </CardHeader>
          <CardBody>
            <GridContainer>
              <GridItem xs={6}>
                <CustomInput
                  labelText="Search"
                  formControlProps={{
                    fullWidth: true
                  }}
                  inputProps={{
                    onChange: handleSearchStringChanged,
                    value: payload.searchString
                  }}
                />
              </GridItem>
              <GridItem xs={6}>
                <FormControl fullWidth className={classes.selectFormControl}>
                  <InputLabel
                    htmlFor="selStatus"
                    className={classes.selectLabel}
                  >
                    Status
                  </InputLabel>
                  <Select
                    MenuProps={{
                      className: classes.selectMenu
                    }}
                    classes={{
                      select: classes.select
                    }}
                    value={payload.status ?? ""}
                    onChange={handleStatusOptionChanged}
                  >
                    {GetOnlineOrderStatuses().map(onlineOrderStatus => (
                      <MenuItem
                        classes={{
                          root: classes.selectMenuItem,
                          selected: classes.selectMenuItemSelected
                        }}
                        value={onlineOrderStatus.key}
                        key={onlineOrderStatus.description}
                      >
                        <p>{onlineOrderStatus.description}</p>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem xs={6}>
                <FormControl fullWidth>
                  <Datetime
                    value={payload.minTransactionTime}
                    onChange={handleMinTransactionTimeChanged}
                    timeFormat={false}
                    closeOnSelect
                    inputProps={{
                      placeholder: "Min Transaction Time"
                    }}
                  />
                </FormControl>
              </GridItem>
              <GridItem xs={6}>
                <FormControl fullWidth>
                  <Datetime
                    value={payload.maxTransactionTime}
                    onChange={handleMaxTransactionTimeChanged}
                    timeFormat={false}
                    closeOnSelect
                    inputProps={{
                      placeholder: "Max Transaction Time"
                    }}
                  />
                </FormControl>
              </GridItem>
            </GridContainer>
            <ReactTable
              manual
              pages={pages}
              page={pagination.pageIndex - 1}
              pageSize={pagination.pageSize}
              onPageChange={page =>
                setPagination({
                  pageIndex: page + 1,
                  pageSize: pagination.pageSize
                })
              }
              onPageSizeChange={(pageSize, page) => {
                setPagination({ pageSize: pageSize, pageIndex: page + 1 });
              }}
              loading={loading}
              data={data}
              filterable={false}
              columns={[
                {
                  id: "bookingNo",
                  Header: "Booking No",
                  accessor: d => d.no
                },
                {
                  id: "transactionTime",
                  Header: "Transaction Time",
                  accessor: d =>
                    formatDateTime(new Date(d.transactionTime + "Z"))
                },
                {
                  id: "status",
                  Header: "Status",
                  accessor: d => GetOnlineOrderStatusDescription(d.status)
                },
                {
                  id: "voucherName",
                  Header: "Voucher Type",
                  accessor: d => (d.voucherType ? d.voucherType.name : "-")
                },
                {
                  id: "user",
                  Header: "Customer",
                  accessor: d => (d.user ? d.user.name : "-")
                },
                {
                  id: "email",
                  Header: "Email",
                  accessor: d => (d.user ? d.user.email : "-")
                },
                {
                  id: "quantity",
                  Header: "Quantity",
                  accessor: d => d.quantity
                },
                {
                  id: "unitPrice",
                  Header: "Unit Price",
                  accessor: d =>
                    d.currency
                      ? formatCurrency(d.currency.code, d.unitPrice)
                      : "XXX"
                },
                {
                  id: "total",
                  Header: "Total",
                  accessor: d =>
                    d.currency
                      ? formatCurrency(d.currency.code, d.total)
                      : "XXX"
                },
                {
                  Header: "Actions",
                  accessor: "actions",
                  sortable: false,
                  filterable: false
                }
              ]}
              defaultPageSize={10}
              showPaginationTop
              showPaginationBottom={false}
              className="-striped -highlight"
            />
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}
